<template>
  <div class="flex flex-col h-full" data-private>
    <!-- Header -->
    <div class="flex items-center justify-between px-4 pt-4 pb-3 border-b border-gray-100">
      <div>
        <h2 class="text-lg font-semibold text-gray-900">EHR Agentic Parse</h2>
        <p class="text-xs text-gray-500 mt-0.5">
          Candidate care gap closures identified from EHR encounter notes and vitals/labs, validated against clinical specifications.
        </p>
      </div>
      <div v-if="total" class="flex items-center gap-3">
        <div class="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2.5 py-1">
          <span class="font-semibold text-gray-900">{{ total.toLocaleString() }}</span> closures
          <span class="text-gray-300 mx-1.5">|</span>
          <span class="font-semibold text-gray-900">{{ patients.toLocaleString() }}</span> patients
        </div>
        <button
          @click="exportCsv"
          class="text-xs font-medium bg-white border border-gray-300 rounded px-3 py-1.5 hover:bg-gray-50 text-gray-700 shadow-sm transition"
        >
          <font-awesome-icon :icon="['fas', 'download']" class="mr-1 text-gray-400" />
          Export CSV
        </button>
      </div>
    </div>

    <!-- Filter Bar -->
    <div class="px-4 py-2.5 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center gap-4">
      <div v-if="practices.length > 1" class="flex items-center gap-1.5">
        <span class="text-xs text-gray-500 font-medium">Practice</span>
        <select
          v-model="filterPractice"
          class="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 min-w-[15rem]"
        >
          <option :value="''">All Practices ({{ total.toLocaleString() }})</option>
          <option v-for="p in practices" :key="p.TIN" class="sensitive" :value="p.PRACTICE_NAME">
            {{ p.PRACTICE_NAME }} ({{ p.CLOSURES.toLocaleString() }})
          </option>
        </select>
      </div>

      <div v-if="contracts.length > 1" class="flex items-center gap-1.5">
        <span class="text-xs text-gray-500 font-medium">Contract / Payer</span>
        <select
          v-model="filterContract"
          class="text-xs border border-gray-300 rounded px-2 py-1.5 bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-1 focus:ring-primary-500 min-w-[12rem]"
        >
          <option :value="''">All Contracts</option>
          <option v-for="c in contracts" :key="c.CONTRACT_NAME" :value="c.CONTRACT_NAME">
            {{ c.CONTRACT_NAME }} ({{ c.CLOSURES.toLocaleString() }})
          </option>
        </select>
      </div>

      <!-- Measure Pill Filters -->
      <div v-if="byMeasure.length" class="flex items-center gap-1.5 ml-auto">
        <button
          @click="filterMeasure = ''"
          class="text-xs rounded px-2 py-1 border transition"
          :class="!filterMeasure
            ? 'bg-gray-800 text-white border-gray-800 font-medium'
            : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'"
        >
          All Measures
        </button>
        <button
          v-for="m in byMeasure" :key="m.MEASURE_CODE"
          @click="filterMeasure = filterMeasure === m.MEASURE_CODE ? '' : m.MEASURE_CODE"
          class="text-xs rounded border px-2 py-1 transition flex items-center gap-1"
          :class="filterMeasure === m.MEASURE_CODE
            ? 'bg-primary-50 border-primary-500 text-primary-800 font-medium ring-1 ring-primary-500'
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'"
        >
          <span>{{ m.MEASURE_CODE }}</span>
          <span class="text-[10px] px-1 py-0.2 rounded font-semibold"
                :class="filterMeasure === m.MEASURE_CODE ? 'bg-primary-200 text-primary-900' : 'bg-gray-100 text-gray-600'">
            {{ m.CLOSURES }}
          </span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="px-4 py-6 text-sm text-gray-500">
      Loading extracted gap closures…
    </div>
    <div v-else-if="error" class="px-4 py-6 text-sm text-red-600">{{ error }}</div>
    <div v-else-if="!rows.length" class="px-4 py-6 text-sm text-gray-500">
      No extracted gap closures available.
    </div>

    <div v-else class="flex-1 min-h-0 flex gap-0">
      <div
        class="min-h-0 overflow-auto w-full transition-all duration-300"
        :style="{ width: selectedPatient ? '50%' : '100%' }"
      >
      <table class="w-full text-xs border-collapse table-fixed">
        <colgroup>
          <col style="width: 14%;" />
          <col style="width: 9%;" />
          <col style="width: 7%;" />
          <col style="width: 14%;" />
          <col style="width: 7%;" />
          <col style="width: 7%;" />
          <col style="width: 22%;" />
          <col style="width: 20%;" />
        </colgroup>
        <thead class="sticky top-0 bg-gray-50 z-10 shadow-sm">
          <tr class="text-left text-gray-600">
            <th class="px-2.5 py-2 font-semibold border-b border-gray-200">Practice / Contract</th>
            <th class="px-2 py-2 font-semibold border-b border-gray-200">Measure</th>
            <th class="px-2 py-2 font-semibold border-b border-gray-200">Gap Status</th>
            <th class="px-2.5 py-2 font-semibold border-b border-gray-200">Patient</th>
            <th class="px-2 py-2 font-semibold border-b border-gray-200">Evidence Date</th>
            <th class="px-2 py-2 font-semibold border-b border-gray-200">Confidence</th>
            <th class="px-2.5 py-2 font-semibold border-b border-gray-200">Clinical Evidence (Quote)</th>
            <th class="px-2.5 py-2 font-semibold border-b border-gray-200">Rule Logic</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(r, i) in filteredRows" :key="i"
            class="align-top border-b border-gray-100 transition-colors"
            :class="selectedPatient && selectedPatient.PATIENT_IDENTIFIER === r.PATIENT_IDENTIFIER
              ? 'bg-blue-50' : (i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40') + ' hover:bg-gray-100/60'"
          >
            <!-- Practice / Contract -->
            <td class="px-2.5 py-2 text-gray-800 break-words">
              <div class="font-medium text-gray-900 leading-snug sensitive">{{ r.PRACTICE_NAME }}</div>
              <div class="text-[11px] text-gray-500 mt-0.5">TIN <span class="sensitive">{{ r.TIN }}</span> · <span class="font-medium text-gray-600">{{ r.CONTRACT_NAME }}</span></div>
            </td>

            <!-- Measure -->
            <td class="px-2 py-2">
              <span class="inline-block rounded bg-gray-100 border border-gray-200 px-1.5 py-0.5 font-semibold text-gray-800 text-[11px]">
                {{ r.MEASURE_CODE }}
              </span>
              <div class="text-[10px] text-gray-500 mt-0.5 leading-tight truncate" :title="r.MEASURE_NAME">{{ r.MEASURE_NAME }}</div>
            </td>

            <!-- Gap Status -->
            <td class="px-2 py-2">
              <span class="inline-block rounded px-1.5 py-0.5 text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                {{ r.GAP_STATUS || 'Open' }}
              </span>
              <div v-if="r.GAP_VALID_FROM" class="text-[10px] text-gray-400 mt-0.5">
                since {{ String(r.GAP_VALID_FROM).slice(0, 10) }}
              </div>
            </td>

            <!-- Patient -->
            <td class="px-2.5 py-2 text-gray-800 break-words">
              <div>
                <button
                  type="button"
                  class="sensitive text-primary-600 hover:text-primary-800 hover:underline font-semibold text-left leading-tight"
                  title="Open this patient's care gaps"
                  @click="openPatient(r)"
                >{{ r.PATIENT_NAME }}</button>
              </div>
              <div class="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1.5">
                <span class="sensitive font-mono text-gray-600">{{ r.PATIENT_IDENTIFIER }}</span>
                <span>·</span>
                <span>{{ r.PATIENT_AGE }}{{ sexLabel(r.PATIENT_SEX) }}</span>
              </div>
            </td>

            <!-- Evidence Date -->
            <td class="px-2 py-2 text-gray-700 whitespace-nowrap">
              <div class="font-medium text-gray-900">{{ r.EVIDENCE_DATE }}</div>
              <div class="text-[10px] text-gray-400 truncate" :title="r.SERVICE_FOUND">{{ r.SERVICE_FOUND }}</div>
            </td>

            <!-- Match / Confidence -->
            <td class="px-2 py-2">
              <span
                class="inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
                :class="r.LLM_CONFIDENCE === 'high'
                  ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                  : 'bg-gray-100 text-gray-700 border border-gray-200'"
              >
                {{ r.LLM_CONFIDENCE || 'HIGH' }}
              </span>
              <div class="text-[10px] text-gray-400 mt-0.5 truncate" :title="r.EVIDENCE_SOURCE">{{ r.MATCH_METHOD }}</div>
            </td>

            <!-- Clinical Evidence Quote -->
            <td class="px-2.5 py-2 break-words">
              <div class="font-mono text-[11px] text-gray-900 bg-amber-50/80 border-l-2 border-amber-400 pl-2 pr-1 py-1 rounded-r leading-relaxed">
                &ldquo;{{ r.EXACT_EVIDENCE_QUOTE }}&rdquo;
              </div>
            </td>

            <!-- Rule Logic -->
            <td class="px-2.5 py-2 text-gray-600 break-words leading-relaxed text-[11px]">
              <div class="font-medium text-gray-800">{{ r.QUALIFYING_RULE_SHORT }}</div>
            </td>
          </tr>
        </tbody>
      </table>
      </div>

      <transition
        enter-active-class="transition-all duration-300"
        leave-active-class="transition-all duration-300"
        enter-from-class="opacity-0 translate-x-4"
        leave-to-class="opacity-0 translate-x-4"
      >
        <div
          v-if="selectedPatient"
          class="flex flex-col overflow-hidden border-l border-gray-200"
          style="width: 50%"
        >
          <Patient_Drawer
            :key="selectedPatient.PATIENT_IDENTIFIER"
            :patient-identifier="selectedPatient.PATIENT_IDENTIFIER"
            :patient-name="selectedPatient.PATIENT_NAME"
            :enabled-tabs="['details', 'utilization', 'gaps', 'engagement', 'care_team']"
            :default-tab="'gaps'"
            :show-header="true"
            @close="selectedPatient = null"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';

export default {
  name: 'Quality_AgenticParse',
  components: { Patient_Drawer },
  data() {
    return {
      rows: [],
      byMeasure: [],
      practices: [],
      contracts: [],
      total: 0,
      patients: 0,
      loading: false,
      error: '',
      filterMeasure: '',
      filterPractice: '',
      filterContract: '',
      selectedPatient: null,
    };
  },
  computed: {
    filteredRows() {
      return this.rows.filter((r) => (
        (!this.filterMeasure || r.MEASURE_CODE === this.filterMeasure)
        && (!this.filterPractice || r.PRACTICE_NAME === this.filterPractice)
        && (!this.filterContract || r.CONTRACT_NAME === this.filterContract)
      ));
    },
  },
  created() {
    this.load();
  },
  methods: {
    openPatient(r) {
      // Open the shared patient drawer in place, on its Care Gaps tab, so the
      // actual open gap sits alongside the extracted evidence.
      this.selectedPatient = (this.selectedPatient
        && this.selectedPatient.PATIENT_IDENTIFIER === r.PATIENT_IDENTIFIER) ? null : r;
    },
    sexLabel(s) {
      const v = String(s || '').trim();
      if (v === '2' || v === '2.0' || v === 'F') return ' / F';
      if (v === '1' || v === '1.0' || v === 'M') return ' / M';
      return '';
    },
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await this.$axios.get('/api/qrda-facts/agentic-parse/');
        this.rows = data.rows || [];
        this.byMeasure = data.byMeasure || [];
        this.practices = data.practices || [];
        this.contracts = data.contracts || [];
        this.total = data.total || 0;
        this.patients = data.patients || 0;
      } catch (e) {
        this.error = 'Unable to load extracted gap closures.';
      } finally {
        this.loading = false;
      }
    },
    exportCsv() {
      const cols = ['PRACTICE_NAME', 'TIN', 'EK_CLIENT_ID', 'CONTRACT_NAME',
        'GAP_STATUS', 'GAP_VALID_FROM', 'MEASURE_CODE', 'MEASURE_NAME',
        'PATIENT_NAME', 'PATIENT_IDENTIFIER', 'PATIENT_AGE', 'PATIENT_SEX',
        'MATCH_METHOD', 'EMPI_PATIENT_MASTER', 'EVIDENCE_SOURCE', 'EVIDENCE_DATE',
        'SERVICE_FOUND', 'COMPLETION_STATUS', 'LLM_CONFIDENCE', 'SOURCE_NOTE_TEXT',
        'EXACT_EVIDENCE_QUOTE', 'QUALIFYING_RULE_SHORT', 'QUALIFYING_RULE_DETAIL'];
      const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
      const lines = [cols.join(',')].concat(
        this.filteredRows.map((r) => cols.map((c) => esc(r[c])).join(',')),
      );
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ehr_agentic_parse_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
  },
};
</script>
